import { memo, useState, useEffect, useCallback, useRef } from "react";
import Form from "../Form.js";
import {
    createMilkRecording,
    updateMilkRecording,
    getUserRole,
    deleteMilkRecording,
    getMilkRecordingCompleteness,
} from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import { Typography, IconButton, Box, Button } from "@mui/material";
import Spinner from "../Spinner.js";
import DeleteIcon from "@mui/icons-material/Delete";
import AreYouSurePopup from "../popups/AreYouSurePopup.js";
import InformPopup from "../popups/InformPopup.js";
import { dayjs } from "../../utils/index.js";
import useSelectStyles from "../styles/Select.js";
import MilkRecordingConfirmation from "../popups/MilkRecordingConfirmation.js";
import MilkMeasurementTransfer from "../popups/MilkMeasurementTransfer.js";
import { formatMilkingTime, getMilkingChecks, isMeasuredMilking, validateMilkingSchedule } from "../../utils/milk-recording-checks.js";

const milkingTypeTranslates = [
    { en: "Hand", gr: "Με το χέρι" },
    { en: "Mechanical", gr: "Μηχανική" },
];

const measurementTypeLabels = {
    morning: "πρωινή",
    noon: "μεσημεριανή",
    evening: "βραδινή",
};

const MilkRecordingForm = ({
    isCreate,
    ownersDropdownValues,
    selectedOwner,
    selectedDate,
    inspectorsDropdownValues,
    defaultInspector,
    setOwnerDateSelected,
    fetchedInspectors,
    selectedMilkRecording,
    setIsOwnerSelected,
    setIsMilkRecordingSelected,
    fetchedMilkCalculation
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);
    const saving = useRef(false);
    const [userRole, setUserRole] = useState(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isUpdateConfirmationOpen, setIsUpdateConfirmationOpen] = useState(false);
    const [pendingUpdateValues, setPendingUpdateValues] = useState(null);
    const [recordingChangeImpact, setRecordingChangeImpact] = useState(null);
    const [changeVersion, setChangeVersion] = useState(null);
    const [dateChangeBlockers, setDateChangeBlockers] = useState([]);
    const [completeness, setCompleteness] = useState(null);
    const [transferOpen, setTransferOpen] = useState(false);

    const classes = useSelectStyles();
    const { success, error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { role } = await getUserRole();
                setUserRole(role);
            } catch (err) {
                error(err)
            }
            setIsLoading(false);
        }
    )
    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (isCreate || !selectedMilkRecording?._id) return;
        const fetchCompleteness = async () => {
            const response = await getMilkRecordingCompleteness(selectedMilkRecording._id);
            if (response.success) setCompleteness(response.completeness);
        };
        fetchCompleteness();
    }, [isCreate, selectedMilkRecording?._id]);

    const existingCheck = (type) => selectedMilkRecording?.checks?.find((check) => check.type === type);
    const existingCheckIsActive = (type) => {
        const check = existingCheck(type);
        return isMeasuredMilking(check);
    };

    const milkRecordingFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
            defaultValue: `${selectedOwner?.lastName} ${selectedOwner?.firstName} - ${selectedOwner?.code}`,
            disabled: true,
        },
        {
            customType: "date-picker",
            id: "testDate",
            type: "desktop",
            label: "Ημ/νια Ελέγχου",
            width: "595px",
            background: "third",
            required: true,
            value: dayjs(selectedDate),
        },
        {
            customType: "dropdown",
            id: "inspector",
            items: inspectorsDropdownValues,
            placeholder: "Ελεγκτής",
            required: true,
            defaultValue: `${defaultInspector?.lastName} ${defaultInspector?.firstName}`,
            disabled: false,
        },
        {
            customType: "dropdown",
            id: "milkingType",
            items: [
                { value: "Μηχανική", text: "Μηχανική" },
                { value: "Με το χέρι", text: "Με το χέρι" },
            ],
            placeholder: "Τρόπος Άμελξης",
            required: true,
            defaultValue: isCreate ? "Μηχανική" : milkingTypeTranslates.find((tr) => tr.en === selectedMilkRecording.milkingType)?.gr,
            disabled: !isCreate && userRole !== "ADMIN",
        },
        {
            customType: 'hour-picker',
            id: 'morningMilkingTime',
            label: 'Ώρα πρωινής άμελξης στην εκτροφή',
            value: isCreate ? null : (selectedMilkRecording.checks.find((check) => check.type === "morning")?.time ? dayjs(new Date(selectedMilkRecording.checks.find((check) => check.type === "morning")?.time)) : ""),
            disabled: false,
        },
        {
            customType: 'checkbox',
            id: 'morningMilkingTypeCheck',
            label: 'Μετρήθηκε η πρωινή άμελξη στον έλεγχο',
            defaultValue: isCreate ? null : existingCheckIsActive("morning"),
            disabled: false,
        },
        {
            customType: 'hour-picker',
            id: 'noonMilkingTime',
            label: 'Ώρα μεσημεριανής άμελξης στην εκτροφή',
            value: isCreate ? null : (selectedMilkRecording.checks.find((check) => check.type === "noon")?.time ? dayjs(new Date(selectedMilkRecording.checks.find((check) => check.type === "noon")?.time)) : ""),
            disabled: false,
        },
        {
            customType: 'checkbox',
            id: 'noonMilkingTypeCheck',
            label: 'Μετρήθηκε η μεσημεριανή άμελξη στον έλεγχο',
            defaultValue: isCreate ? null : existingCheckIsActive("noon"),
            disabled: false,
        },
        {
            customType: 'hour-picker',
            id: 'eveningMilkingTime',
            label: 'Ώρα βραδινής άμελξης στην εκτροφή',
            value: isCreate ? null : (selectedMilkRecording.checks.find((check) => check.type === "evening")?.time ? dayjs(new Date(selectedMilkRecording.checks.find((check) => check.type === "evening")?.time)) : ""),
            disabled: false,
        },
        {
            customType: 'checkbox',
            id: 'eveningMilkingTypeCheck',
            label: 'Μετρήθηκε η βραδινή άμελξη στον έλεγχο',
            defaultValue: isCreate ? null : existingCheckIsActive("evening"),
            disabled: false,
        },
        {
            customType: "input",
            id: "animalsNumber",
            type: "number",
            placeholder: "Αριθμός Ζώων",
            value: isCreate ? null : fetchedMilkCalculation?.animalsNumber,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "input",
            id: "milkQuantity",
            type: "number",
            placeholder: "Συνολική ημερήσια ποσότητα γάλακτος (g)",
            value: isCreate ? null : fetchedMilkCalculation?.totalMilkCalculation,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "input",
            id: "milkFat",
            type: "number",
            placeholder: "Λίπος παραγώμενου γάλακτος",
            value: isCreate ? null : fetchedMilkCalculation?.milkFat,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "input",
            id: "milkProtein",
            type: "number",
            placeholder: "Πρωτεΐνη παραγώμενου γάλακτος",
            value: isCreate ? null : fetchedMilkCalculation?.milkProtein,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "input",
            id: "milkLactose",
            type: "number",
            placeholder: "Λακτόζη παραγώμενου γάλακτος",
            value: isCreate ? null : fetchedMilkCalculation?.milkLactose,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "input",
            id: "cells",
            type: "number",
            placeholder: "Σωματικά Κύτταρα(x1000) παραγώμενου γάλακτος",
            value: isCreate ? null : fetchedMilkCalculation?.cells,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "input",
            id: "microbialLoad",
            type: "number",
            placeholder: "Ολικό Μικροβιακό Φορτίο παραγώμενου γάλακτος",
            value: isCreate ? null : fetchedMilkCalculation?.microbialLoad,
            disabled: true,
            isHidden: isCreate ? true : false
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ]

    const milkRecordingSubmit = (values) => {
        const checks = getMilkingChecks(values);
        // Permit unrelated edits to historical records with an incomplete schedule.
        const scheduleChanged = isCreate || checks.some((check) => {
            const existing = existingCheck(check.type);
            const timeKey = (time) => !time ? "" : Number.isNaN(new Date(time).getTime()) ? "invalid" : formatMilkingTime(time);
            return timeKey(check.time) !== timeKey(existing?.time)
                || check.checked !== isMeasuredMilking(existing);
        });
        const scheduleError = scheduleChanged ? validateMilkingSchedule(checks) : null;
        if (scheduleError) {
            error(scheduleError);
            return;
        }
        const selectedInspector = fetchedInspectors.find(
            (inspector) => `${inspector.lastName} ${inspector.firstName}` === values.inspector
        );
        const valuesToSend = {
            ...values,
            owner: selectedOwner._id,
            inspector: selectedInspector?._id || null,
            checks,
        };
        if (isCreate) {
            setPendingSubmission(valuesToSend);
        } else {
            saveMilkRecording(valuesToSend);
        }
    };

    const saveMilkRecording = async (valuesToSend, confirmed = false, version = null) => {
        if (saving.current) return;
        saving.current = true;
        setPendingSubmission(null);
        setIsLoading(true);
        try {
            let response;
            if (isCreate) {
                response = await createMilkRecording(valuesToSend);
            } else {
                response = await updateMilkRecording(selectedMilkRecording._id, valuesToSend, confirmed, confirmed, confirmed, version);
                if (response.requiresRecordingChangeConfirmation || response.requiresDateChangeConfirmation) {
                    setPendingUpdateValues(valuesToSend);
                    setRecordingChangeImpact(response.recordingChangeImpact || null);
                    setChangeVersion(response.changeVersion);
                    if (response.message) error(response.message);
                    setIsUpdateConfirmationOpen(true);
                    return;
                }
            }

            const { success: scs, message } = response;
            if (scs) {
                success(message);
                setIsUpdateConfirmationOpen(false);
                if (isCreate) {
                    setOwnerDateSelected(false);
                } else {
                    setIsOwnerSelected(false);
                    setIsMilkRecordingSelected(false);
                }
            } else {
                if (response.blockers?.length > 0) {
                    setDateChangeBlockers(response.blockers);
                } else {
                    error(message);
                }
            }

        } catch (err) {
            error(err?.message || "Η αποθήκευση απέτυχε. Παρακαλώ προσπαθήστε ξανά.");
        } finally {
            saving.current = false;
            setIsLoading(false);
        }
    };

    const confirmRecordingChange = () => {
        if (pendingUpdateValues) saveMilkRecording(pendingUpdateValues, true, changeVersion);
    };

    const handleDeleteMilkRecording = async () => {
        setIsLoading(true);
        const { success: scs, message } = await deleteMilkRecording(selectedMilkRecording._id);
        if (scs) {
            success(message);
            setIsMilkRecordingSelected(false);
            setIsOwnerSelected(false);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            {transferOpen && <MilkMeasurementTransfer
                recordingId={selectedMilkRecording._id}
                onClose={() => setTransferOpen(false)}
                onTransferred={(response) => {
                    success(response.message);
                    setTransferOpen(false);
                    setIsOwnerSelected(false);
                    setIsMilkRecordingSelected(false);
                }}
            />}
            <MilkRecordingConfirmation
                values={pendingSubmission}
                ownerName={`${selectedOwner?.lastName} ${selectedOwner?.firstName} - ${selectedOwner?.code}`}
                onCancel={() => setPendingSubmission(null)}
                onConfirm={() => pendingSubmission && saveMilkRecording(pendingSubmission)}
            />
            <AreYouSurePopup
                width={"900px"}
                isOpen={isPopupOpen}
                title="Διαγραφή Γαλακτοπαραγωγής"
                titleColor="black"
                onClose={() => setIsPopupOpen(false)}
                innerText={`Είστε σίγουροι ότι θέλετε να διαγράψετε τη γαλακτοπαραγωγή για την ημερομηνία "${new Date(selectedMilkRecording?.testDate).toLocaleDateString("el-GR")}";`}
                declineText="Πίσω"
                onDecline={() => setIsPopupOpen(false)}
                acceptText="Ναι, Διαγραφή Γαλακτοπαραγωγής"
                onAccept={() => handleDeleteMilkRecording()}
            />
            <AreYouSurePopup
                width={"900px"}
                isOpen={isUpdateConfirmationOpen}
                title="Επιβεβαίωση αλλαγών ελέγχου"
                titleColor="black"
                onClose={() => setIsUpdateConfirmationOpen(false)}
                innerText={(
                    <Box sx={{ maxHeight: "calc(100vh - 240px)", overflowY: "auto" }}>
                        {recordingChangeImpact?.previousDate && (
                            <Typography>
                                Η ημερομηνία θα αλλάξει από {new Date(recordingChangeImpact.previousDate).toLocaleDateString("el-GR")}
                                {" σε "}{new Date(recordingChangeImpact.newDate).toLocaleDateString("el-GR")}.
                            </Typography>
                        )}
                        {(recordingChangeImpact?.updatedChecks || []).map((check) => {
                            const before = recordingChangeImpact.previousChecks?.find((item) => item.type === check.type);
                            const time = (value) => value ? formatMilkingTime(value) : "Δεν πραγματοποιείται";
                            return <Typography key={check.type} sx={{ mt: 1 }}>
                                {measurementTypeLabels[check.type]}: {time(before?.time)} → {time(check.time)}.
                                {" "}Μετρήθηκε: {before?.checked ? "Ναι" : "Όχι"} → {check.checked ? "Ναι" : "Όχι"}.
                            </Typography>;
                        })}
                        {recordingChangeImpact?.addedTypes?.length > 0 && (
                            <Typography sx={{ mt: 2, fontWeight: 700, color: "error.main" }}>
                                Προσθήκη μετρημένης άμελξης: {recordingChangeImpact.addedTypes.map((type) => measurementTypeLabels[type]).join(", ")}.
                                {" "}Είστε υπεύθυνοι να ενημερώσετε τις καταγραφές και των {recordingChangeImpact.affectedAnimals} ζώων
                                με τις νέες ποσότητες πριν από τον επανυπολογισμό.
                            </Typography>
                        )}
                        {recordingChangeImpact?.removedTypes?.length > 0 && (
                            <Typography sx={{ mt: 2, fontWeight: 700, color: "error.main" }}>
                                Αφαίρεση μετρημένης άμελξης: {recordingChangeImpact.removedTypes.map((type) => measurementTypeLabels[type]).join(", ")}.
                                {" "}Οι ποσότητες αυτών των αμέλξεων θα διαγραφούν αυτόματα από τις καταγραφές των ζώων
                                ({recordingChangeImpact.removedQuantities} καταχωρημένες ποσότητες).
                            </Typography>
                        )}
                        {recordingChangeImpact?.legacyDailyTotalAnimals > 0 && (
                            <Typography sx={{ mt: 2, fontWeight: 700, color: "error.main" }}>
                                {recordingChangeImpact.legacyDailyTotalAnimals} ιστορικές εγγραφές έχουν μόνο ημερήσιο σύνολο.
                                Το σύνολο θα αφαιρεθεί επειδή δεν διαχωρίζεται ανά άμελξη· πρέπει να συμπληρώσετε τις ποσότητες των μετρημένων αμέλξεων.
                            </Typography>
                        )}
                        <Typography sx={{ mt: 2, fontWeight: 700 }}>
                            {recordingChangeImpact?.recalculationNotice}
                            {" "}Οι συνολικές παραγωγές δεν θα επανυπολογιστούν αυτόματα.
                        </Typography>
                        {(recordingChangeImpact?.warnings || []).map((warning) => <Typography key={warning} sx={{ mt: 1 }}>{warning}</Typography>)}

                    </Box>
                )}
                declineText="Ακύρωση"
                onDecline={() => setIsUpdateConfirmationOpen(false)}
                acceptText="Επιβεβαίωση αλλαγής"
                onAccept={confirmRecordingChange}
            />
            <InformPopup
                width="900px"
                isOpen={dateChangeBlockers.length > 0}
                title="Η ημερομηνία δεν μπορεί να αλλάξει"
                titleColor="black"
                onClose={() => setDateChangeBlockers([])}
                innerText={dateChangeBlockers}
                onAccept={() => setDateChangeBlockers([])}
                acceptText="Εντάξει"
                cancelable={false}
            />
            <Spinner open={isLoading} />
            <Box className={classes.outerBox}>
                <Box flexDirection="column" className={classes.innerBox}>

                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        {isCreate ? "Εισάγετε τα στοιχεία της γαλακτοπαραγωγής" : "Στοιχεία γαλακτοπαραγωγής"}
                    </Typography>
                    <Box sx={{ margin: "0 1rem 1rem", padding: "1rem", backgroundColor: "third.main", borderRadius: 1 }}>
                        <Typography sx={{ fontWeight: 700 }}>Καθημερινό πρόγραμμα άμελξης και μετρήσεις ελέγχου</Typography>
                        <Typography>
                            Συμπληρώστε τις ώρες όλων των καθημερινών αμέλξεων της εκτροφής (2 ή 3),
                            ακόμη και αν δεν μετρήθηκαν στον συγκεκριμένο έλεγχο.
                            Επιλέξτε «Μετρήθηκε» μόνο για τις αμέλξεις των οποίων καταγράφηκε η ποσότητα γάλακτος.
                            Αφήστε κενή την ώρα μόνο για άμελξη που δεν πραγματοποιείται στην εκτροφή.
                        </Typography>
                        {!isCreate && <Typography sx={{ mt: 1, fontWeight: 700 }}>
                            Μπορείτε να αλλάξετε τις ώρες και τις μετρημένες αμέλξεις.
                            Μετά από αλλαγές, είστε υπεύθυνοι να ενημερώσετε τις μετρήσεις των ζώων
                            και να εκτελέσετε ξανά τους υπολογισμούς πραγματικής και τυπικής γαλακτοπαραγωγής.
                        </Typography>}
                        <Typography sx={{ mt: 1 }}>
                            Η ημερήσια ποσότητα υπολογίζεται όπως στο παλιό σύστημα Αμνός:
                            άθροισμα όταν μετρήθηκαν όλες οι αμέλξεις, διαφορετικά αναγωγή της μετρημένης
                            ποσότητας σε 24 ώρες βάσει του προγράμματος άμελξης του συγκεκριμένου ελέγχου.
                        </Typography>
                    </Box>
                    {!isCreate && completeness && !completeness.complete && (
                        <Box sx={{ margin: "0 1rem 1rem", padding: "1rem", color: "error.main", fontWeight: 700 }}>
                            Ο έλεγχος δεν είναι πλήρης: εκκρεμούν {completeness.incompleteAnimals} ζώα.
                            {Object.entries(completeness.missingByType || {})
                                .filter(([, count]) => count > 0)
                                .map(([type, count]) => ` ${measurementTypeLabels[type] || type}: ${count}`)
                                .join(" ·")}
                        </Box>
                    )}
                    {!isCreate && fetchedMilkCalculation?.incompleteAnimals > 0 && (
                        <Typography color="error" sx={{ mx: 2, mb: 2 }}>
                            Το ημερήσιο σύνολο δεν είναι διαθέσιμο: {fetchedMilkCalculation.incompleteAnimals} ζώα δεν έχουν υπολογίσιμη ημερήσια ποσότητα.
                        </Typography>
                    )}
                    {!isCreate && fetchedMilkCalculation?.estimatedAnimals > 0 && (
                        <Typography sx={{ mx: 2, mb: 2 }}>
                            Το ημερήσιο σύνολο περιλαμβάνει εκτίμηση για {fetchedMilkCalculation.estimatedAnimals} ζώα με μερική μέτρηση,
                            βάσει των χρονικών διαστημάτων άμελξης, όπως στο παλιό σύστημα Αμνός.
                        </Typography>
                    )}
                    {!isCreate && <Box sx={{ mx: 2, mb: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        <Typography sx={{ fontWeight: 700 }}>Οι ποσότητες μπήκαν σε λάθος άμελξη;</Typography>
                        <Typography sx={{ my: 1 }}>
                            Μεταφέρετε τις μετρήσεις όλων των ζώων σε άλλη άμελξη, χωρίς να ξαναπεράσετε τις ποσότητες.
                            Η μεταφορά χρησιμοποιεί τα αποθηκευμένα στοιχεία. Αν έχετε άλλες αλλαγές στη φόρμα, αποθηκεύστε τις πρώτα.
                        </Typography>
                        <Button variant="outlined" disabled={isLoading} onClick={() => setTransferOpen(true)}>Μεταφορά μετρήσεων</Button>
                    </Box>}
                    <Form
                        content={milkRecordingFormContent}
                        toResetForm={false}
                        onSubmit={milkRecordingSubmit} />
                    {isCreate && (
                        <Button
                            onClick={() => setOwnerDateSelected(false)}
                            sx={{
                                marginTop: "1rem",
                                padding: "0.5rem 1rem",
                                backgroundColor: "third",
                                color: "secondary.main",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                variant: "h5",
                            }}
                        >
                            Πίσω
                        </Button>
                    )}
                    {!isCreate && (
                        <Button
                            onClick={() => setIsMilkRecordingSelected(false)}
                            sx={{
                                marginTop: "1rem",
                                padding: "0.5rem 1rem",
                                backgroundColor: "third",
                                color: "secondary.main",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                variant: "h5",
                            }}
                        >
                            Πίσω
                        </Button>
                    )}
                    {!isCreate && completeness?.totalAnimals === 0 ? (
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <IconButton
                                aria-label="delete"
                                onClick={() => setIsPopupOpen(true)}
                            >
                                <DeleteIcon />
                            </IconButton >
                        </Box>
                    ) : (
                        <></>
                    )}

                </Box>
            </Box>
        </>
    )
}

export default memo(MilkRecordingForm);
